'use client';

import { startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { useGetApiTips } from '@/api/backoffice/tips/tips';
import Calendar from '../calendar';
import { EmptyState } from '../EmptyState';
import { Pagination } from '../pagination';
import SkeletonEvent from '../skeletons/event';
import { HorizontalTipCard } from './HorizontalTipCard';
import {
  buildTipsFeedQueryParams,
  createInitialFeedState,
  type FeedState,
  getSkeletonCount,
  shouldShowInitialLoading,
} from './tipsFeed.utils';

export type TipsFeedWithCalendarProps = {
  type: 'finished' | 'not_started';
  tipsterId?: string;
};

export const AdvancedTipsFeed = ({
  type,
  tipsterId,
}: TipsFeedWithCalendarProps) => {
  const [state, setState] = useState<FeedState>(() =>
    createInitialFeedState(type)
  );

  const queryParams = useMemo(
    () => buildTipsFeedQueryParams(type, state, tipsterId),
    [type, state, tipsterId]
  );

  const { data, isLoading } = useGetApiTips(queryParams);

  return (
    <div className="flex flex-col gap-3">
      {type === 'not_started' && (
        <div className="flex flex-col gap-3 rounded-lg bg-card-bg p-4">
          <h3 className="font-semibold text-base text-gray">
            Filter tips by day
          </h3>
          <Calendar
            onChange={(newDate) => {
              setState((prev) => ({
                ...prev,
                date: new Date(startOfDay(newDate)),
                page: 1,
              }));
            }}
            type="future"
          />
        </div>
      )}

      {shouldShowInitialLoading(
        isLoading,
        type,
        data?.data?.items?.length ?? 0
      ) &&
        Array.from({ length: getSkeletonCount(type) }, (_, index) => (
          <SkeletonEvent
            key={`skeleton-${
              // biome-ignore lint/suspicious/noArrayIndexKey: rendering skeleton
              index
            }`}
          />
        ))}

      {!isLoading && (data?.data?.items?.length ?? 0) > 0
        ? data?.data?.items?.map((item) => (
            <HorizontalTipCard data={item} key={item.id} />
          ))
        : !isLoading && (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-card-bg px-3 py-5">
              <EmptyState
                description="We add tips on regular basis but on somedays theres just no juice. Make sure to check back tomorrow."
                title={
                  type === 'finished'
                    ? 'No tips for finished matches yet.'
                    : 'No tips for this day'
                }
              />
            </div>
          )}

      {data &&
        (data.data.totalCount ?? 0) > 0 &&
        (data.data.totalPages ?? 0) > 1 && (
          <Pagination
            currentPage={data.data.pageNumber ?? 1}
            onChange={(newPage) => {
              setState((prev) => ({
                ...prev,
                page: newPage,
              }));
            }}
            perPageItems={queryParams.PageSize}
            totalCount={data.data.totalCount ?? 0}
            totalPages={data.data.totalPages ?? 1}
          />
        )}
    </div>
  );
};
